import cloneDeep from 'lodash/cloneDeep';

import { checkCanary, createThunkRequest, getCanaryKey, POST } from '@webapp/account/src/libs/request';
import {
    PROFILE_CHANGE_PASSWORD,
    PROFILE_CONFIRM_EMAIL,
    PROFILE_CONFIRM_RESET_PASSWORD,
    PROFILE_RESEND_CONFIRM_EMAIL,
} from '@webapp/account/src/resources/profile/constants';
import { initialLicense, mapLicense, mapQuotas } from '@webapp/account/src/resources/profile/license';
import { mapProfile, reverseMapProfile } from '@webapp/account/src/resources/profile/map';
import { initialProfile } from '@webapp/account/src/resources/profile/profile';
import { AccountRoutes } from '@webapp/account/src/resources/routes';

import { createFingerprint, getFingerprint } from '@webapp/common/lib/api';
import { delCookieWithSubdomains, setUserToken } from '@webapp/common/lib/cookies';
import { LocalStorage, LocalStorageKeys } from '@webapp/common/lib/storage';
import { getUrlSearchParam, undefDestroy } from '@webapp/common/lib/utils';

export const authorize = createThunkRequest(
    'profile/authorize',
    async ({ email, password }: { email: string; password: string }) => {
        const fingerprint = getFingerprint();
        const { confirm_type, device_confirmed, license, profile, token } = await POST(
            'authorization',
            {
                user: email,
                password,
                fingerprint,
            },
            { withoutToken: true },
        );

        setUserToken(token);

        const userId = profile?.[0]?.USER_ID;

        // Check canary status after successful login and reload if user is in canary
        if (userId) {
            const savedUserId = LocalStorage.get<number>(LocalStorageKeys.ACCOUNT_USER_ID);
            const isCanary = LocalStorage.get(getCanaryKey(userId));
            if (savedUserId && savedUserId !== userId && !isCanary) {
                delCookieWithSubdomains('is_canary');
            }

            await checkCanary(userId);
        }

        const profileData = {
            ...profile?.[0],
            device_confirmed,
            confirm_type,
        };
        const backUrl = LocalStorage.get<string>(LocalStorageKeys.BACK_URL);
        const url = getUrlSearchParam('backUrl', backUrl) || AccountRoutes.account.link;

        return {
            profile: {
                ...cloneDeep(initialProfile),
                ...(device_confirmed ? mapProfile(profileData) : profileData),
            },
            license: {
                ...cloneDeep(initialLicense),
                ...(device_confirmed ? mapLicense(license) : license),
            },
            url,
        };
    },
    { notify: false, notifyError: true },
);

export const getProfile = createThunkRequest(
    'profile/getProfile',
    async (_, { getState }) => {
        const resp = await POST('get/profile');

        const profileData = {
            ...(resp.profile?.[0] || {}),
            restricted: Boolean(resp.restricted),
            ...resp,
        };
        const prevLicence = getState().Profile.license;
        return {
            profile: {
                ...cloneDeep(initialProfile),
                ...mapProfile(profileData),
            },
            license: prevLicence.licenseId ? prevLicence : cloneDeep(initialLicense),
        };
    },
    { notify: false, notifyError: true },
);

export const getQuotas = createThunkRequest(
    'profile/getQuotas',
    async () => {
        const resp = await POST('show/quotas');

        const license = {
            ...cloneDeep(initialLicense),
            ...mapLicense(resp.license),
            surveys: mapQuotas(resp.surveys),
        };

        return { license };
    },
    { notify: false, notifyError: true },
);

export const confirmDevice = createThunkRequest(
    'profile/confirmDevice',

    async () => {
        const fingerprint = getFingerprint();

        const { confirm_type, message } = await POST('device/send', {
            fingerprint,
        });

        return {
            confirm_type,
            message,
        };
    },
);

export const confirmDeviceHashCode = createThunkRequest(
    'profile/confirmDeviceHash',
    async ({ code, hash }: { code: string; hash: string }) => {
        const { message } = await POST(
            'device/confirmation',
            undefDestroy({
                hash,
                code,
            }),
        );

        return { message };
    },
);

export const register = createThunkRequest(
    'profile/register',
    async ({ email, name, password }: { email: string; name: string; password: string }) => {
        const referer = localStorage.getItem('referrer') || undefined;
        const fingerprint = createFingerprint();

        const { token, message } = await POST(
            'registration',
            undefDestroy({
                user: email,
                fingerprint,
                password,
                name,
                referer,
            }),
        );

        setUserToken(token);

        return { url: AccountRoutes.account.link, message };
    },
);

export const confirmEmail = createThunkRequest(
    PROFILE_CONFIRM_EMAIL,

    async ({ token }: { token: string }) => {
        setUserToken(token);

        const fingerprint = getFingerprint();

        const { token: newToken } = await POST('confirmation', { fingerprint });

        setUserToken(newToken);

        return {};
    },
);

export const resetPassword = createThunkRequest(
    'profile/resetPassword',

    async ({ email }: { email: string }) => {
        const { message } = await POST('reset/password', { user: email });

        return { message };
    },
);

export const saveProfile = createThunkRequest(
    'profile/saveProfile',

    async ({ data }: { data: any }) => {
        const { message } = await POST('set/user_info', {
            data: JSON.stringify(reverseMapProfile(data)),
        });

        return { profile: data, message };
    },
);

export const confirmResetPassword = createThunkRequest(
    PROFILE_CONFIRM_RESET_PASSWORD,
    async ({ token }: { token: string }) => {
        setUserToken(token);
        const { token: newToken, message } = await POST('confirm/reset');
        setUserToken(newToken);

        return { message };
    },
);

export const changePassword = createThunkRequest(
    PROFILE_CHANGE_PASSWORD,

    async ({ password }: { password: string }) => {
        const { message } = await POST('change/password', { password });

        return { message };
    },
);

export const resendConfirmEmail = createThunkRequest(
    PROFILE_RESEND_CONFIRM_EMAIL,

    async () => {
        const { message } = await POST('resend');

        return { message };
    },
);

export const changeEmailUnconfirmed = createThunkRequest(
    'profile/changeEmailUnconfirmed',
    async ({ email }: { email: string }) => {
        const { token, message } = await POST('reset/email', { email });

        setUserToken(token);

        return { message };
    },
);

export const createPayment = createThunkRequest(
    'profile/createPayment',

    async (data: { data: any }) => {
        return POST('create/payment', data, { json: true });
    },
);
